import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Plus, Clock } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useSearchParams } from "react-router-dom";

const STATUS_COLOR: Record<string, string> = {
  submitted: "bg-amber-100 text-amber-700",
  approved:  "bg-green-100 text-green-700",
  rejected:  "bg-red-100 text-red-700",
};

const ContractLaborLogHours = () => {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  const defaultWoId = searchParams.get("wo") ?? "";
  const isMobile = useIsMobile();

  const [woId, setWoId] = useState(defaultWoId);
  const [hoursDate, setHoursDate] = useState(new Date().toISOString().split("T")[0]);
  const [regularHours, setRegularHours] = useState("8");
  const [overtimeHours, setOvertimeHours] = useState("0");
  const [notes, setNotes] = useState("");

  const { data: workOrders = [] } = useQuery({
    queryKey: ["cl_log_hours_wos", user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("work_orders")
        .select("id, number, projects(name)")
        .in("status", ["open", "in_progress"] as any)
        .order("created_at", { ascending: false });
      return data ?? [];
    },
    enabled: !!user,
  });

  const { data: recentEntries = [] } = useQuery({
    queryKey: ["cl_recent_hours", user?.id],
    queryFn: async () => {
      const { data } = await (supabase
        .from("labor_hours" as any) as any)
        .select("id, work_order_id, hours_date, regular_hours, overtime_hours, notes, status, created_at, work_orders(number)")
        .eq("worker_user_id", user!.id)
        .order("created_at", { ascending: false })
        .limit(20);
      return data ?? [];
    },
    enabled: !!user,
  });

  const submit = useMutation({
    mutationFn: async () => {
      if (!woId) throw new Error("Select a work order");
      const reg = parseFloat(regularHours);
      const ot = parseFloat(overtimeHours);
      if (isNaN(reg) || reg < 0) throw new Error("Enter valid regular hours");
      if (isNaN(ot) || ot < 0) throw new Error("Enter valid overtime hours");
      if (reg + ot === 0) throw new Error("Total hours must be greater than 0");

      const { data: companyId } = await supabase.rpc("my_company_id");

      const { error } = await (supabase.from("labor_hours" as any) as any).insert({
        company_id: companyId,
        work_order_id: woId,
        worker_user_id: user!.id,
        hours_date: hoursDate,
        regular_hours: reg,
        overtime_hours: ot,
        notes: notes.trim() || null,
        status: "submitted",
      } as any);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cl_recent_hours"] });
      toast.success("Hours submitted successfully");
      setRegularHours("8");
      setOvertimeHours("0");
      setNotes("");
    },
    onError: (err: any) => toast.error(err.message),
  });

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-bold">Log Hours</h1>

      {/* Entry Form */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Plus className="h-4 w-4" /> Submit Hours
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Work Order *</Label>
            <Select value={woId} onValueChange={setWoId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a work order" />
              </SelectTrigger>
              <SelectContent>
                {workOrders.map((wo: any) => (
                  <SelectItem key={wo.id} value={wo.id}>
                    {wo.number} {wo.projects?.name ? `— ${wo.projects.name}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Date *</Label>
            <Input type="date" value={hoursDate} onChange={(e) => setHoursDate(e.target.value)} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Regular Hours *</Label>
              <Input type="number" min="0" max="24" step="0.25" value={regularHours} onChange={(e) => setRegularHours(e.target.value)} />
            </div>
            <div>
              <Label>Overtime Hours</Label>
              <Input type="number" min="0" max="24" step="0.25" value={overtimeHours} onChange={(e) => setOvertimeHours(e.target.value)} />
            </div>
          </div>

          <div>
            <Label>Notes (optional)</Label>
            <Textarea placeholder="Work performed today…" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
          </div>

          <Button onClick={() => submit.mutate()} disabled={submit.isPending || !woId}>
            {submit.isPending ? (
              <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Submitting…</>
            ) : (
              <><Clock className="h-4 w-4 mr-1" />Submit Hours</>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Recent Entries */}
      <div>
        <h2 className="text-base font-semibold mb-3">Recent Entries</h2>
        {recentEntries.length === 0 ? (
          <p className="text-muted-foreground text-sm">No hours submitted yet.</p>
        ) : isMobile ? (
          <div className="space-y-3">
            {recentEntries.map((entry: any) => (
              <Card key={entry.id}>
                <CardContent className="p-3 space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{entry.hours_date}</span>
                    <Badge variant="outline" className={STATUS_COLOR[entry.status] ?? ""}>
                      {entry.status}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground font-mono">
                    {entry.work_orders?.number ? `WO-${entry.work_orders.number}` : "—"}
                  </p>
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex gap-3 text-muted-foreground">
                      <span>Reg: {entry.regular_hours}h</span>
                      <span>OT: {entry.overtime_hours ?? 0}h</span>
                    </div>
                  </div>
                  {entry.notes && (
                    <p className="text-xs text-muted-foreground truncate">{entry.notes}</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Work Order</TableHead>
                  <TableHead className="text-right">Reg</TableHead>
                  <TableHead className="text-right">OT</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentEntries.map((entry: any) => (
                  <TableRow key={entry.id}>
                    <TableCell className="text-sm">{entry.hours_date}</TableCell>
                    <TableCell className="font-mono text-sm">{entry.work_orders?.number ?? "—"}</TableCell>
                    <TableCell className="text-right text-sm">{entry.regular_hours}h</TableCell>
                    <TableCell className="text-right text-sm">{entry.overtime_hours ?? 0}h</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={STATUS_COLOR[entry.status] ?? ""}>
                        {entry.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">{entry.notes ?? "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContractLaborLogHours;
